import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/apiClient";
import { CAMPAIGN, FAQS } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { IndiaStateCountMap } from "@/components/IndiaStateCountMap";
import { PledgeCard } from "@/components/PledgeCard";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, ShieldCheck, FileText, Users, Trophy, HeartPulse } from "lucide-react";

export default function LandingPage() {
  const [stats, setStats] = useState(null);
  const [pledges, setPledges] = useState([]);

  const load = () => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/pledges/recent?limit=30").then((r) => setPledges(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const steps = [
    { Icon: FileText, title: "Fill the form", desc: "Share your name, council registration number and specialty." },
    { Icon: HeartPulse, title: "Take the pledge", desc: "Read the Healthy India pledge and confirm with one click." },
    { Icon: ShieldCheck, title: "Get your certificate", desc: "Download a personalized certificate instantly with a unique ID." },
  ];

  return (
    <div className="App bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 hero-noise opacity-30" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/85 to-[#0056D2]/40" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white mb-6"
              >
                <Trophy className="w-4 h-4 text-[#FF9933]" />
                Asia Book of Records Attempt · {CAMPAIGN.dates}
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
              >
                One nation.<br />One pledge.<br />
                <span className="text-[#FF9933]">A healthier India.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed"
              >
                Join medical professionals across India in a nationwide pledge to serve the health and
                well-being of the nation. Add your name to the wall and receive your certificate.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8 flex flex-wrap gap-4"
              >
                <Link to="/pledge" data-testid="hero-pledge-cta">
                  <Button size="lg" className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold px-7 h-12 text-base">
                    Take the Pledge <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/wall" data-testid="hero-wall-cta">
                  <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white hover:text-slate-900 font-semibold px-7 h-12 text-base">
                    View Live Wall
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-6 lg:p-8"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-5 font-semibold">Live Participation — Statewise</p>
              <IndiaStateCountMap breakdown={stats?.state_breakdown || []} dark />
            </motion.div>
          </div>
        </div>
      </section>

      {/* LIVE SCROLLING STRIP */}
      {pledges.length > 0 && (
        <section className="bg-slate-900 py-6 border-y border-slate-800">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {pledges.map((p) => <PledgeCard key={p.id} pledge={p} dark />)}
          </Marquee>
        </section>
      )}

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#0056D2] font-semibold mb-3">About the initiative</p>
            <h2 className="font-display font-extrabold text-slate-900 text-3xl lg:text-4xl tracking-tight leading-tight">
              A movement of medical professionals for a healthier nation
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              The Healthy India Pledge Wall unites doctors from every corner of the country in a shared
              commitment to preventive care, ethical practice, and compassionate service. Every pledge
              strengthens a nationwide movement — and contributes to an official Asia Book of Records attempt.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { Icon: Users, label: "For every doctor", sub: "Across all states & UTs" },
                { Icon: ShieldCheck, label: "Self-declared", sub: "Simple, fast, trusted" },
                { Icon: Trophy, label: "Record attempt", sub: "Asia Book of Records" },
              ].map(({ Icon, label, sub }) => (
                <div key={label} className="rounded-xl border border-slate-200 p-4">
                  <Icon className="w-6 h-6 text-[#138808] mb-2" />
                  <p className="font-display font-bold text-slate-900 text-sm">{label}</p>
                  <p className="text-xs text-slate-500">{sub}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Doctor in white coat"
              className="rounded-3xl w-full h-[440px] object-cover border border-slate-200"
            />
            <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl bg-white border border-slate-200 shadow-xl p-5">
              <p className="font-display font-extrabold text-3xl text-[#0056D2]">
                {(stats?.total_pledges ?? 0).toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Pledges & counting</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 py-20 lg:py-28 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF9933] font-semibold mb-3">How it works</p>
            <h2 className="font-display font-extrabold text-slate-900 text-3xl lg:text-4xl tracking-tight">
              Three simple steps to pledge
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {steps.map(({ Icon, title, desc }, i) => (
              <div key={title} data-testid={`how-step-${i}`} className="rounded-2xl bg-white border border-slate-200 p-8">
                <div className="flex items-center justify-between mb-5">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#0056D2] text-white">
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="font-display font-extrabold text-5xl text-slate-100">0{i + 1}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900">{title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/pledge">
              <Button size="lg" className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold px-7 h-12">
                Start your pledge <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <p className="text-xs uppercase tracking-[0.25em] text-[#0056D2] font-semibold mb-3 text-center">FAQs</p>
        <h2 className="font-display font-extrabold text-slate-900 text-3xl lg:text-4xl tracking-tight text-center mb-10">
          Frequently asked questions
        </h2>
        <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-xl px-5">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-semibold text-slate-900 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CONTACT / CTA */}
      <section className="bg-slate-950 relative overflow-hidden">
        <div className="tricolor-bar h-1 w-full" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tight">
            Be part of the record. Take the pledge.
          </h2>
          
          <Link to="/pledge" className="inline-block mt-8">
            <Button size="lg" className="rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold px-8 h-13 text-base h-12">
              I Pledge Now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
